import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  Package,
  Download,
  Truck,
  ExternalLink,
  ShieldCheck,
  Store as StoreIcon,
} from "lucide-react";
import { api, getUser, rupiah } from "../lib/api";
import { Header, Footer, Loading, Empty } from "../components/Shell";

/* -------------------------------------------------------------------------- */
/*  Public: /store catalog                                                    */
/* -------------------------------------------------------------------------- */
export function StoreLanding() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("ALL");
  useEffect(() => {
    api("/store").then(setItems).catch(() => {});
  }, []);
  const filtered = useMemo(
    () => (tab === "ALL" ? items : items.filter((x) => x.type === tab)),
    [items, tab]
  );
  return (
    <div>
      <Header />
      <main className="store-page">
        <header className="store-hero">
          <span className="section-kicker">TOKO MASTOFA</span>
          <h1>Produk Digital pilihan Mas Tofa</h1>
          <p>Ebook, template ads, dan swipe file yang sudah tervalidasi.</p>
          <div className="store-tabs" data-testid="store-tabs">
            {[
              { key: "ALL", label: "Semua" },
              { key: "DIGITAL", label: "Digital" },
            ].map((t) => (
              <button
                key={t.key}
                className={tab === t.key ? "active" : ""}
                onClick={() => setTab(t.key)}
                data-testid={`store-tab-${t.key}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </header>
        {filtered.length === 0 ? (
          <Empty
            icon={<StoreIcon size={38} />}
            title="Produk belum tersedia"
            body="Admin belum publish produk di kategori ini."
            cta={null}
          />
        ) : (
          <div className="store-grid" data-testid="store-grid">
            {filtered.map((p) => (
              <StoreCard key={p.slug} product={p} />
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}

function StoreCard({ product }) {
  return (
    <Link
      to={product.external_link ? "#" : `/store/${product.slug}`}
      onClick={(e) => {
        if (product.external_link) {
          e.preventDefault();
          window.open(product.external_link, "_blank", "noreferrer");
        }
      }}
      className="store-card"
      data-testid={`store-card-${product.slug}`}
    >
      <div className="store-thumb">
        {product.thumbnail ? (
          <img src={product.thumbnail} alt={product.title} />
        ) : (
          <Package size={40} />
        )}
        <span className={`store-type-badge ${product.type}`}>
          {product.type === "DIGITAL" ? <Download size={12} /> : <Truck size={12} />}
          {product.type}
        </span>
      </div>
      <div className="store-info">
        <small>{product.category}</small>
        <h3>{product.title}</h3>
        <div className="store-price">
          {product.price_regular > product.price && (
            <span className="strike">{rupiah(product.price_regular)}</span>
          )}
          <b>{rupiah(product.price)}</b>
        </div>
      </div>
    </Link>
  );
}

/* -------------------------------------------------------------------------- */
/*  Public: /store/:slug detail                                               */
/* -------------------------------------------------------------------------- */
export function StoreDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  useEffect(() => {
    api(`/store/${slug}`).then(setP).catch(() => setP(false));
  }, [slug]);
  if (p === null) return <Loading />;
  if (!p) return <div className="loading">Produk tidak ditemukan.</div>;
  return (
    <div>
      <Header />
      <main className="store-detail" data-testid="store-detail">
        <div className="store-detail-media">
          {p.thumbnail && <img src={p.thumbnail} alt={p.title} />}
          {(p.images || []).map((img, i) => (
            <img key={i} src={img} alt="" />
          ))}
        </div>
        <div className="store-detail-info">
          <span className="section-kicker">{p.category} · {p.type}</span>
          <h1>{p.title}</h1>
          <div className="store-price big">
            {p.price_regular > p.price && (
              <span className="strike">{rupiah(p.price_regular)}</span>
            )}
            <b>{rupiah(p.price)}</b>
          </div>
          {p.type === "PHYSICAL" && (
            <div className="store-stock">
              Stok: <b>{p.stock}</b> · Berat: {p.weight}g
            </div>
          )}
          <div
            className="store-description"
            dangerouslySetInnerHTML={{
              __html: p.description || "<p>Deskripsi produk akan segera hadir.</p>",
            }}
          />
          <div className="store-perks">
            <div><ShieldCheck size={16} /> Garansi kualitas</div>
            <div><CheckCircle2 size={16} /> Pembayaran aman via Duitku</div>
            {p.type === "DIGITAL" ? (
              <div><Download size={16} /> Instant download setelah bayar</div>
            ) : (
              <div><Truck size={16} /> Kirim seluruh Indonesia via Biteship</div>
            )}
          </div>
          {p.external_link ? (
            <a
              href={p.external_link}
              target="_blank"
              rel="noreferrer"
              className="button button-large"
              data-testid="external-buy-btn"
            >
              Beli di Marketplace <ExternalLink size={16} />
            </a>
          ) : (
            <Link
              to={`/store/checkout/${p.slug}`}
              className="button button-large"
              data-testid="buy-now-btn"
            >
              Beli Sekarang <ArrowRight size={16} />
            </Link>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Store Checkout                                                            */
/* -------------------------------------------------------------------------- */
export function StoreCheckout() {
  const { slug } = useParams();
  const nav = useNavigate();
  const user = getUser();
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);
  const [form, setForm] = useState({
    payment_method: "",
    whatsapp: "",
    receiver_name: user?.name || "",
    receiver_phone: "",
    receiver_email: user?.email || "",
    address: "",
    city: "",
    postal_code: "",
  });
  const [methods, setMethods] = useState({ available: false, methods: [] });
  const [rates, setRates] = useState([]);
  const [selRate, setSelRate] = useState(null);
  const [terms, setTerms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [rateBusy, setRateBusy] = useState(false);

  useEffect(() => {
    api(`/store/${slug}`).then(setP).catch(() => setP(false));
  }, [slug]);

  const subtotal = p ? Number(p.price) * qty : 0;
  const shippingFee = selRate?.price || 0;
  const total = subtotal + (p?.type === "PHYSICAL" ? shippingFee : 0);

  useEffect(() => {
    if (!total) return;
    api(`/payments/methods?amount=${total}`).then(setMethods).catch(() => {});
  }, [total]);

  if (!user) {
    nav(`/login?next=/store/checkout/${slug}`, { replace: true });
    return null;
  }
  if (p === null) return <Loading />;
  if (!p) return <div className="loading">Produk tidak ditemukan.</div>;

  const loadRates = async () => {
    setError("");
    if (!form.postal_code) {
      setError("Isi kode pos dulu.");
      return;
    }
    setRateBusy(true);
    try {
      const r = await api("/store/rates", {
        method: "POST",
        data: {
          product_slug: slug,
          quantity: qty,
          destination_postal_code: Number(form.postal_code),
        },
      });
      setRates(r.pricing || []);
      if (!r.biteship_configured) {
        setError(
          "Biteship belum dikonfigurasi. Admin perlu isi BITESHIP_API_KEY untuk hitung ongkir."
        );
      } else if ((r.pricing || []).length === 0) {
        setError("Belum ada kurir tersedia untuk kode pos tersebut.");
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Gagal ambil ongkir.");
    } finally {
      setRateBusy(false);
    }
  };

  const submit = async () => {
    setError("");
    if (!terms) return setError("Centang persetujuan syarat.");
    if (!form.payment_method) return setError("Pilih metode pembayaran.");
    if (p.type === "PHYSICAL") {
      if (!form.receiver_name || !form.receiver_phone || !form.address || !form.postal_code)
        return setError("Data pengiriman wajib lengkap.");
      if (!selRate) return setError("Pilih kurir dulu.");
    }
    setBusy(true);
    try {
      const body = {
        product_slug: slug,
        quantity: qty,
        payment_method: form.payment_method,
        whatsapp: form.whatsapp || form.receiver_phone,
      };
      if (p.type === "PHYSICAL") {
        Object.assign(body, {
          receiver_name: form.receiver_name,
          receiver_phone: form.receiver_phone,
          receiver_email: form.receiver_email,
          address: form.address,
          city: form.city,
          postal_code: Number(form.postal_code),
          courier_company: selRate.company,
          courier_type: selRate.courier_service_code,
          shipping_fee: Number(selRate.price),
        });
      }
      const r = await api("/payments/checkout-store", { method: "POST", data: body });
      window.mastofaTrack?.("Purchase", { value: total, currency: "IDR" });
      if (r.payment_url) window.location.href = r.payment_url;
      else nav("/dashboard/store", { replace: true });
    } catch (e) {
      setError(e?.response?.data?.detail || "Checkout gagal.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <Header />
      <main className="checkout-main" data-testid="store-checkout">
        <div className="checkout-left">
          <span className="section-kicker">Toko · Checkout</span>
          <h1>{p.title}</h1>
          <div className="checkout-block">
            <b>1. Jumlah</b>
            <input
              type="number"
              min="1"
              max={p.type === "PHYSICAL" ? p.stock || 99 : 1}
              value={qty}
              onChange={(e) => setQty(Math.max(1, Number(e.target.value)))}
              data-testid="input-qty"
              disabled={p.type === "DIGITAL"}
            />
            {p.type === "DIGITAL" && (
              <small>Produk digital 1× lisensi per pembelian.</small>
            )}
          </div>

          {p.type === "PHYSICAL" && (
            <>
              <div className="checkout-block">
                <b>2. Alamat pengiriman</b>
                <input
                  placeholder="Nama penerima"
                  value={form.receiver_name}
                  onChange={(e) => setForm({ ...form, receiver_name: e.target.value })}
                  data-testid="input-receiver"
                />
                <input
                  placeholder="No HP penerima"
                  value={form.receiver_phone}
                  onChange={(e) => setForm({ ...form, receiver_phone: e.target.value })}
                  data-testid="input-phone"
                />
                <input
                  placeholder="Alamat lengkap"
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  data-testid="input-address"
                />
                <div className="row-2">
                  <input
                    placeholder="Kota"
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                    data-testid="input-city"
                  />
                  <input
                    placeholder="Kode pos"
                    inputMode="numeric"
                    value={form.postal_code}
                    onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
                    data-testid="input-postal"
                  />
                </div>
                <button
                  className="button button-small"
                  onClick={loadRates}
                  disabled={rateBusy}
                  data-testid="load-rates-btn"
                >
                  {rateBusy ? "Menghitung..." : "Hitung ongkir"}
                </button>
              </div>

              <div className="checkout-block">
                <b>3. Pilih kurir</b>
                {rates.length === 0 ? (
                  <small>Belum ada. Isi kode pos lalu klik &quot;Hitung ongkir&quot;.</small>
                ) : (
                  <div className="rate-list" data-testid="rate-list">
                    {rates.map((r, i) => {
                      const key = `${r.company}-${r.courier_service_code}-${i}`;
                      const active =
                        selRate?.company === r.company &&
                        selRate?.courier_service_code === r.courier_service_code;
                      return (
                        <label
                          key={key}
                          className={`rate-card ${active ? "active" : ""}`}
                          data-testid={`rate-${r.company}-${r.courier_service_code}`}
                        >
                          <input
                            type="radio"
                            name="rate"
                            checked={active}
                            onChange={() => setSelRate(r)}
                          />
                          <div>
                            <b>{r.courier_name}</b> · {r.courier_service_name}
                            <small>Estimasi: {r.duration}</small>
                          </div>
                          <b>{rupiah(r.price)}</b>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}

          <div className="checkout-block">
            <b>{p.type === "PHYSICAL" ? "4" : "2"}. Metode pembayaran</b>
            {!methods.available ? (
              <small>{methods.message || "Memuat..."}</small>
            ) : (
              <div className="method-grid" data-testid="method-grid">
                {methods.methods.map((m) => (
                  <label
                    key={m.paymentMethod}
                    className={`method-card ${form.payment_method === m.paymentMethod ? "active" : ""}`}
                    data-testid={`pm-${m.paymentMethod}`}
                  >
                    <input
                      type="radio"
                      name="pm"
                      checked={form.payment_method === m.paymentMethod}
                      onChange={() => setForm({ ...form, payment_method: m.paymentMethod })}
                    />
                    <img src={m.paymentImage} alt={m.paymentName} />
                    <span>{m.paymentName}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <label className="terms">
            <input
              type="checkbox"
              checked={terms}
              onChange={(e) => setTerms(e.target.checked)}
              data-testid="terms-check"
            />
            Saya setuju dengan syarat &amp; ketentuan
          </label>
          {error && <div className="error">{error}</div>}
          <button
            className="button button-large"
            onClick={submit}
            disabled={busy}
            data-testid="store-pay-btn"
          >
            {busy ? "Memproses..." : `Bayar ${rupiah(total)}`}
          </button>
        </div>

        <aside className="checkout-summary">
          <h4>Ringkasan</h4>
          <div className="checkout-row">
            <span>
              {p.title} × {qty}
            </span>
            <b>{rupiah(subtotal)}</b>
          </div>
          {p.type === "PHYSICAL" && (
            <div className="checkout-row">
              <span>Ongkir {selRate ? `(${selRate.courier_name})` : ""}</span>
              <b>{rupiah(shippingFee)}</b>
            </div>
          )}
          <hr />
          <div className="checkout-row total">
            <span>Total</span>
            <b>{rupiah(total)}</b>
          </div>
        </aside>
      </main>
      <Footer />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Member Store Orders + Downloads                                           */
/* -------------------------------------------------------------------------- */
export function MyStoreOrders() {
  const [orders, setOrders] = useState([]);
  useEffect(() => {
    api("/me/store/orders").then(setOrders).catch(() => {});
  }, []);
  const download = async (orderId) => {
    try {
      const r = await api(`/me/store/download/${orderId}`);
      if (r.url) window.open(r.url, "_blank");
    } catch (e) {
      alert(e?.response?.data?.detail || "Link belum tersedia.");
    }
  };
  return (
    <div>
      <Header />
      <main className="store-orders" data-testid="my-store-orders">
        <h1>Transaksi Toko</h1>
        {orders.length === 0 ? (
          <Empty
            icon={<Package size={38} />}
            title="Belum ada transaksi toko"
            body="Cek katalog toko untuk mulai belanja."
            cta={<Link to="/store" className="button">Ke toko <ArrowRight size={16} /></Link>}
          />
        ) : (
          <div className="orders-list">
            {orders.map((o) => (
              <div key={o.id} className={`order-card status-${o.status}`} data-testid={`order-${o.id}`}>
                <div>
                  <b>{o.product_title}</b>
                  <small>
                    {o.merchant_order_id} · {new Date(o.created_at).toLocaleDateString("id-ID")}
                  </small>
                </div>
                <div className="order-mid">
                  <span className="badge">{o.status}</span>
                  <b>{rupiah(o.amount)}</b>
                </div>
                <div className="order-actions">
                  {o.status === "PAID" && (o.store?.type === "DIGITAL") && (
                    <button
                      className="button button-small"
                      onClick={() => download(o.id)}
                      data-testid={`dl-${o.id}`}
                    >
                      <Download size={14} /> Download
                    </button>
                  )}
                  {o.status === "PAID" && o.store?.type === "PHYSICAL" && (
                    <div className="tracking">
                      {o.biteship?.waybill_id ? (
                        <>
                          Resi: <b>{o.biteship.waybill_id}</b>
                          <small>{o.biteship?.status || "Sedang diproses"}</small>
                        </>
                      ) : (
                        <small>Menunggu pickup kurir.</small>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
